import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import LazyImage from '@/components/LazyImage';
import SEO from '@/components/SEO';
import { seoData } from '@/utils/seoData';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Target, Heart, TrendingUp, Quote } from 'lucide-react';

export const AboutPage = () => {
  const values = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Customer First',
      description: 'We prioritize customer satisfaction in everything we do, ensuring quality service and transparent communication.',
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: 'Quality Excellence',
      description: 'We use only genuine parts and employ skilled mechanics to deliver superior automotive solutions.',
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Expert Team',
      description: 'Our certified mechanics bring years of experience and expertise to every repair job.',
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Continuous Improvement',
      description: 'We stay updated with the latest automotive technologies and best practices.',
    },
  ];

  const milestones = [
    { year: '2014', event: 'Company Founded' },
    { year: '2016', event: 'Expanded Service Center' },
    { year: '2019', event: '5000+ Happy Customers' },
    { year: '2024', event: 'Leading Auto Parts Provider' },
  ];

  const testimonials = [
    {
      name: 'Ahmed Hassan',
      role: 'Toyota Owner',
      text: 'Best auto service in Pakistan! The team is professional and honest. They fixed my engine issue in no time.',
      rating: 5,
    },
    {
      name: 'Fatima Khan',
      role: 'Honda Customer',
      text: 'Excellent service and genuine parts. I trust them completely with my car maintenance. Highly recommended!',
      rating: 5,
    },
    {
      name: 'Imran Ali',
      role: 'Suzuki Owner',
      text: 'Very professional staff and fair pricing. They explained everything clearly and the repair quality is outstanding.',
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-background noise-bg">
      <SEO 
        title={seoData.about.title}
        description={seoData.about.description}
        keywords={seoData.about.keywords}
        ogImage={seoData.about.ogImage}
        structuredData={seoData.about.structuredData}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              About <span className="gradient-text">Bilal Ganj Auto Parts</span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Your trusted partner in automotive excellence. We've been serving Pakistan with quality auto parts and professional repair services for over a decade.
            </p>
          </div>
        </div>
      </section>

      {/* CEO Message Section */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden">
                  <LazyImage
                    src="/images/bilalceo.jpg"
                    alt="Muhammad Bilal - CEO Bilal Ganj Auto Parts"
                    className="w-full h-auto object-cover"
                    dualRingLoader={true}
                    fadeInDuration={0.8}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent"></div>
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="text-2xl font-bold text-foreground mb-1">Muhammad Bilal</h3>
                  <p className="text-primary font-medium">Founder & CEO</p>
                </div>
              </div>
              <div>
                <div className="mb-6">
                  <Quote className="w-12 h-12 text-primary/30" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-6">Message from Our CEO</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    Welcome to Bilal Ganj Auto Parts. For over a decade, we have been committed to providing 
                    exceptional automotive services and genuine parts to our valued customers across Pakistan.
                  </p>
                  <p>
                    Our journey began with a simple vision: to create a service center where quality, 
                    honesty, and customer satisfaction come first. Today, I'm proud to say that we've built 
                    a reputation based on trust and excellence.
                  </p>
                  <p>
                    We believe that every vehicle deserves the best care, and every customer deserves 
                    transparent, professional service. Our team of certified mechanics works tirelessly 
                    to ensure your vehicle performs at its best.
                  </p>
                  <p className="text-foreground font-medium italic">
                    "Quality is not an act, it is a habit. We make quality our daily commitment to you."
                  </p>
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="text-foreground font-semibold">Muhammad Bilal</p>
                  <p className="text-sm text-muted-foreground">Founder & CEO, Bilal Ganj Auto Parts</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Founded in 2014, Bilal Ganj Auto Parts began with a simple mission: to provide 
                  Pakistan's automotive community with reliable parts and expert repair services at fair prices.
                </p>
                <p>
                  What started as a small workshop has grown into one of the region's most trusted 
                  automotive service providers. Today, we serve thousands of satisfied customers, 
                  from individual car owners to commercial fleets.
                </p>
                <p>
                  Our commitment to quality, transparency, and customer satisfaction has remained 
                  unchanged since day one. We continue to invest in our team's training, upgrade 
                  our equipment, and expand our inventory to better serve you.
                </p>
              </div>
            </div>
            <div className="relative">
              <LazyImage
                src="/images/workshop.jpg"
                alt="Our Workshop"
                className="rounded-lg shadow-lg w-full"
                pulseLoader={true}
                fadeInDuration={0.7}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Our Values
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              These core values guide everything we do and shape our company culture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="card-3d bg-background border-border hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6 flex flex-col h-full">
                  <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Our Journey
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A decade of growth, innovation, and dedication to automotive excellence.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary/20 hidden md:block"></div>

              {milestones.map((milestone, index) => (
                <div
                  key={index}
                  className={`relative mb-12 md:mb-20 flex flex-col md:flex-row items-center ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className="w-full md:w-5/12 mb-4 md:mb-0">
                    <Card className="card-3d bg-card border-border hover:border-primary/50 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="text-3xl font-bold text-primary mb-2">{milestone.year}</div>
                        <h3 className="text-xl font-semibold">{milestone.event}</h3>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="w-6 h-6 rounded-full bg-primary border-4 border-background absolute left-1/2 transform -translate-x-1/2 hidden md:block"></div>
                  <div className="w-full md:w-5/12"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-32 bg-card">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it - hear from our satisfied customers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="card-3d bg-background border-border hover:border-primary/50 transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-5 h-5 text-accent fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                  </div>
                  <Quote className="w-8 h-8 text-primary/30 mb-4" />
                  <p className="text-muted-foreground mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div className="pt-4 border-t border-border">
                    <p className="font-semibold text-foreground">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Meet Our Expert Team
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Our team of certified mechanics and automotive specialists are dedicated to providing 
              you with the best service possible. With years of combined experience, we handle everything 
              from routine maintenance to complex repairs.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center text-primary text-2xl font-bold">
                  50+
                </div>
                <p className="text-sm text-muted-foreground">Mechanics</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center text-primary text-2xl font-bold">
                  100%
                </div>
                <p className="text-sm text-muted-foreground">Certified</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center text-primary text-2xl font-bold">
                  10+
                </div>
                <p className="text-sm text-muted-foreground">Years Exp.</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center text-primary text-2xl font-bold">
                  24/7
                </div>
                <p className="text-sm text-muted-foreground">Support</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;